import { Snackbar, SnackbarContent } from "@material-ui/core";
import { Map } from "ol";
import { getCenter, intersects } from "ol/extent";
import Feature from "ol/Feature";
import VectorSource from "ol/source/Vector";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdContentPaste, MdContentPasteGo } from "react-icons/md";

import {
  setIconsGroup,
  setSelectedFeature,
  setSelectedFeatures,
} from "../../model/app/actions";
import clearSelectedFeatures, {
  getModifyControl,
} from "../../utils/clearSelectedFeatures";
import cloneLayer from "../../utils/cloneLayer";
import {
  EDIT_LAYER_NAME,
  OLD_STYLES_FUNCTION_PROP,
} from "../../utils/constants";
import {
  useAppDispatch,
  useAppSelector,
  useLayer,
} from "../../utils/customHooks";
import getMaxZIndex from "../../utils/getMaxZIndex";
import isTextInputFocused from "../../utils/isTextInputFocused";
import readKml from "../../utils/readKml";
import writeKml from "../../utils/writeKml";

const COPY_MESSAGE = "Element wurde in die Zwischenablage kopiert";
const PASTE_MESSAGE = "Kopiertes Element wurde eingefügt";
const INITIAL_DELTA = 10;

const getTranslatedFeatures = (
  features: Feature[],
  map: Map,
  delta: number,
  eventType: string,
) => {
  if (!features || !map) {
    return [];
  }
  return features.map((feature) => {
    const clone = feature.clone();
    const newGeometry = clone.getGeometry().clone();
    if (eventType === "cut") {
      // When cutting, the features are pasted at the center of the map
      const tempSource = new VectorSource({ features });
      const mapCenter = map?.getView().getCenter();
      const deltaX = mapCenter[0] - getCenter(tempSource.getExtent())[0];
      const deltaY = mapCenter[1] - getCenter(tempSource.getExtent())[1];
      newGeometry.translate(deltaX, deltaY);
    } else {
      // When copying, the features are pasted with an offset
      const res = map.getView().getResolution();
      newGeometry.translate(delta * res, -delta * res);
    }
    clone.setGeometry(newGeometry);
    return clone;
  });
};

export const MapsetAllowedProp = {
  EvtType: "mapset.evtType",
} as const;
export type MapsetAllowedProp =
  (typeof MapsetAllowedProp)[keyof typeof MapsetAllowedProp];

const useCopyPaste = (): [
  null | string,
  React.Dispatch<React.SetStateAction<null | string>>,
] => {
  const dispatch = useAppDispatch();
  const selectedFeature = useAppSelector((state) => {
    return state.app.selectedFeature;
  });
  const iconsGroup = useAppSelector((state) => {
    return state.app.iconsGroup;
  });
  const editLayer = useLayer(EDIT_LAYER_NAME);
  const olMap = useAppSelector((state) => {
    return state.map.olMap;
  });
  const editor = useAppSelector((state) => {
    return state.app.editor;
  });
  const [message, setMessage] = useState<null | string>(null);
  const delta = useRef(INITIAL_DELTA);

  useEffect(() => {
    const copyListener = (evt: ClipboardEvent) => {
      const selectedFeatures = (
        iconsGroup?.length > 0 ? iconsGroup : [selectedFeature]
      ).filter(Boolean);

      if (!isTextInputFocused() && selectedFeatures?.length > 0) {
        const featuresToCopy = selectedFeatures.map((feature) => {
          const clone = feature.clone();
          clone.setStyle(feature.get(OLD_STYLES_FUNCTION_PROP));
          // Keep info about the event type to handle cut vs copy in the paste listener
          clone.set(MapsetAllowedProp.EvtType, evt.type);
          return clone;
        });

        if (evt.type === "cut") {
          selectedFeatures.forEach((feat) => {
            editLayer.getSource().removeFeature(feat);
          });
          dispatch(setSelectedFeature());
          dispatch(setIconsGroup());
          dispatch(setSelectedFeatures());
        }

        // Write data from the clipboard as KML string
        const newLayer = cloneLayer(editLayer, olMap);
        newLayer.getSource().clear();
        newLayer.getSource().addFeatures(featuresToCopy);

        const kml = writeKml(newLayer, olMap, 0);
        if (kml) {
          navigator.clipboard.writeText(kml);
        }

        // Update snackbar message and delta for the next paste
        setMessage(COPY_MESSAGE);
        delta.current = INITIAL_DELTA;
      }
    };

    const pasteListener = async () => {
      let clipboardDataFeatures = [];

      // Read data from the clipboard and check if it is KML
      const data = await navigator.clipboard.readText();
      if (/^<kml/.test(data) && /<\/kml>$/.test(data)) {
        const features = readKml(data, olMap);
        clipboardDataFeatures = features;
      }

      if (!isTextInputFocused() && clipboardDataFeatures?.length > 0) {
        // Clear the selected feature and insert translated clone into the map
        dispatch(setSelectedFeature());
        clearSelectedFeatures(editor);

        // All features have the same eventType (cut or copy) because they were copied/cut together
        const eventType = clipboardDataFeatures[0].get(
          MapsetAllowedProp.EvtType,
        );
        const pasteFeatures = getTranslatedFeatures(
          clipboardDataFeatures,
          olMap,
          delta.current,
          eventType,
        );

        // Set the copied features on top of others
        pasteFeatures.forEach((feature, index) => {
          feature.set("zIndex", getMaxZIndex(editLayer) + index + 1);
        });

        editLayer.getSource().addFeatures(pasteFeatures);

        // Pan to pasted features if they are not visible
        const tempSource = new VectorSource({ features: pasteFeatures });
        const pasteExtent = tempSource.getExtent();
        const mapExtent = olMap.getView().calculateExtent();
        const isPasteFeaturesVisible = intersects(pasteExtent, mapExtent);
        if (!isPasteFeaturesVisible) {
          olMap.getView().fit(pasteExtent, {
            duration: 500,
            maxZoom: olMap.getView().getZoom(),
          });
        }

        // Unselect the pasted features
        getModifyControl(editor)?.selectInteraction?.getFeatures().clear();

        if (pasteFeatures.length > 1) {
          dispatch(setIconsGroup(pasteFeatures));
        } else {
          dispatch(setSelectedFeature(pasteFeatures[0]));
        }

        // Update the delta for the next paste
        delta.current += INITIAL_DELTA;
        if (eventType === "cut") {
          delta.current = INITIAL_DELTA;
          // TODO: handle more paste than once after cut
          navigator.clipboard.writeText("");
        }
        setMessage(PASTE_MESSAGE);
      }
    };

    document.addEventListener("copy", copyListener);
    document.addEventListener("cut", copyListener);
    document.addEventListener("paste", pasteListener);

    return () => {
      document.removeEventListener("copy", copyListener);
      document.removeEventListener("cut", copyListener);
      document.removeEventListener("paste", pasteListener);
    };
  }, [dispatch, selectedFeature, editLayer, olMap, iconsGroup, editor, delta]);

  return [message, setMessage];
};

function CopyPasteHandler() {
  const { t } = useTranslation();
  const [message, setMessage] = useCopyPaste();

  if (!message) {
    return null;
  }

  return (
    <Snackbar
      anchorOrigin={{
        horizontal: "center",
        vertical: "bottom",
      }}
      autoHideDuration={1500}
      onClose={() => {
        setMessage(null);
      }}
      open={!!message}
    >
      <SnackbarContent
        message={
          <div style={{ alignItems: "center", display: "flex", gap: 10 }}>
            {message === COPY_MESSAGE ? (
              <MdContentPaste size={20} />
            ) : (
              <MdContentPasteGo size={20} />
            )}
            {t(message)}
          </div>
        }
      />
    </Snackbar>
  );
}

export default CopyPasteHandler;
